import React, {useEffect, useMemo, useState} from 'react';
import {Stack} from '@mui/material';
import {Button, Icon} from '@lightricks/react-design-system';
import AssetCard from '@contentlab/components/asset-card';
import {ViewMode} from '@contentlab/components/asset-card/AssetCard';
import {UploadingFile} from '@contentlab/stores/contentLabStore';
import {Asset} from '@contentlab/types/asset';
import {FeatureName} from '@contentlab/types/project';
import {AssetProps} from '@contentlab/types/projectProps';
import {FEATURE_NAME_TO_METADATA_PROPERTY} from '@contentlab/utils/featureHelpers';
import mapAssetToAssetProps from '@contentlab/utils/mapAssetToAssetProps';
import mapFileToAssetProps from '@contentlab/utils/mapFileToAssetProps';

interface AssetDisplayProps {
  file?: UploadingFile;
  readyAsset?: Asset;
  onClose?: () => void;
  feature: FeatureName;
  setPreviewAsset?: (asset: AssetProps) => void;
  errorCodes: string[];
}

function AssetDisplay({
  file,
  readyAsset,
  onClose,
  feature,
  setPreviewAsset,
  errorCodes,
}: AssetDisplayProps) {
  // this proxy serves as a placeholder as the file itself will eventually be discarded
  const [fileProxy, setFileProxy] = useState<UploadingFile | undefined>(file);
  useEffect(() => {
    if (file) {
      setFileProxy(file);
    }
  }, [file]);

  const assetProps = useMemo(() => {
    if (readyAsset) {
      return mapAssetToAssetProps(
        readyAsset,
        readyAsset.id,
        'inputAsset',
        errorCodes,
        feature
      );
    }
    if (fileProxy) {
      return mapFileToAssetProps(
        fileProxy,
        fileProxy?.id,
        'inputAsset',
        errorCodes
      );
    }
    return undefined;
  }, [fileProxy, readyAsset, errorCodes]);

  return (
    <Stack
      direction="row"
      width="100%"
      boxSizing="border-box"
      sx={{
        position: 'relative',
      }}
    >
      <AssetCard
        asset={assetProps}
        file={readyAsset ? undefined : fileProxy}
        viewMode={ViewMode.Upload}
        metadataDisplayParam={FEATURE_NAME_TO_METADATA_PROPERTY[feature]}
        onThumbnailClick={setPreviewAsset}
      />
      {onClose && (
        <Button
          appearance="neutral"
          mode="elevated"
          size="small"
          sx={{
            position: 'absolute',
            top: -8,
            right: -8,
            zIndex: 10,
            boxShadow:
              '0px 12px 42px -4px rgba(24, 39, 75, 0.12), 0px 4px 16px -6px rgba(24, 39, 75, 0.12)',
          }}
          icon={
            <Icon
              name="Actions-Close-Small"
              size="medium"
              appearance="primary"
            />
          }
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        />
      )}
    </Stack>
  );
}

export default AssetDisplay;
